import { FC, useState, useMemo } from 'react';
import { Stack, Typography, CircularProgress, Grid } from '@mui/material';
import { usePricingData } from '@/hooks/usePricingData';
import { Subscription } from '@/services/noteGoApi/noteGoApiSchemas';
import PlanCard from './PlanCard';
import BillingToggle from './BillingToggle';

/**
 * Props for the `Plans` component.
 *
 * @property handleUpgrade - Callback function invoked when the user chooses to upgrade their plan.
 *   - `frequencyId`: The identifier for the selected billing frequency.
 *   - `subscriptionId`: (Optional) The identifier for the current subscription, if any.
 *   - `stripeCustomerId`: (Optional) The Stripe customer ID, if available.
 * @property onContinue - (Optional) Callback function invoked when the user continues to the next step.
 * @property onDowngradeToFree - (Optional) Callback function invoked when user selects the FREE lifetime plan.
 * @property currentUserPlan - (Optional) The current subscription object for the user, or `null` if none.
 */
interface PlansProps {
  handleUpgrade: (frequencyId: string, subscriptionId?: string | null, stripeCustomerId?: string | null) => void;
  onContinue?: () => void;
  onDowngradeToFree?: (productId?: string) => void;
  currentUserPlan?: Subscription | null;
  isLoading?: boolean;
}
const Plans: FC<PlansProps> = ({
  handleUpgrade,
  onContinue,
  onDowngradeToFree,
  currentUserPlan,
  isLoading = false,
}) => {
  const { pricingData, loading, error } = usePricingData();

  const [selectedFrequency, setSelectedFrequency] = useState<'month' | 'year'>('year');

  // Calculate max savings percentage across all plans
  const maxSavingsPercent = useMemo(() => {
    if (!pricingData) return 0;
    let maxSavings = 0;
    for (const plan of Object.values(pricingData)) {
      if (!plan.active || plan.plan_type === 'lifetime_cap') continue;
      const monthlyPrice = typeof plan.prices?.month?.value === 'number' ? plan.prices.month.value : 0;
      const yearlyPrice = typeof plan.prices?.year?.value === 'number' ? plan.prices.year.value : 0;
      if (monthlyPrice > 0 && yearlyPrice > 0) {
        const annualCostMonthly = monthlyPrice * 12;
        const savings = Math.round(((annualCostMonthly - yearlyPrice) / annualCostMonthly) * 100);
        if (savings > maxSavings) maxSavings = savings;
      }
    }
    return maxSavings;
  }, [pricingData]);

  // Check if any plan has valid yearly pricing
  const hasYearlyPricing = useMemo(() => {
    if (!pricingData) return false;
    return Object.values(pricingData).some((plan) => {
      if (!plan.active || plan.plan_type === 'lifetime_cap') return false;
      const yearlyPrice = plan.prices?.year?.value;
      return yearlyPrice != null && yearlyPrice !== 0;
    });
  }, [pricingData]);

  // Fall back to monthly when yearly pricing is unavailable so paid monthly plans are shown
  const effectiveFrequency = hasYearlyPricing ? selectedFrequency : 'month';

  if (loading || isLoading) {
    return (
      <Stack gap="1.5rem" alignItems="center">
        <Typography variant="h5" fontWeight="600">
          All Plans
        </Typography>
        <CircularProgress />
      </Stack>
    );
  }
  if (error || !pricingData) {
    return (
      <Stack gap="1.5rem" alignItems="center">
        <Typography variant="h5" fontWeight="600">
          All Plans
        </Typography>
        <Typography color="error">Error loading plans: {error}</Typography>
      </Stack>
    );
  }

  return (
    <Stack gap="1.5rem" alignItems="center" sx={{ width: '100%' }}>
      <Typography variant="h5" fontWeight="600">
        All Plans
      </Typography>

      {hasYearlyPricing && (
        <BillingToggle
          selectedFrequency={selectedFrequency}
          onChange={setSelectedFrequency}
          savingsPercent={maxSavingsPercent}
        />
      )}

      <Grid container spacing={{ xs: 2, sm: 2, md: 2, lg: 3 }} justifyContent="center" sx={{ width: '100%' }}>
        {Object.values(pricingData)
          .filter((plan) => {
            if (!plan.active) return false;
            // Include lifetime plans (plan_type is lifetime_cap)
            if (plan.plan_type === 'lifetime_cap') return true;
            // Include rate-based plans with valid pricing
            return plan.prices?.[effectiveFrequency as 'month' | 'year'] != null;
          })
          .sort((a, b) => {
            // Lifetime plans (FREE) come first
            if (a.plan_type === 'lifetime_cap') return -1;
            if (b.plan_type === 'lifetime_cap') return 1;

            const priceA = a.prices?.[effectiveFrequency as 'month' | 'year']?.value ?? 0;
            const priceB = b.prices?.[effectiveFrequency as 'month' | 'year']?.value ?? 0;

            // Handle cases where price is a string (e.g., "Free")
            if (typeof priceA === 'string') return -1;
            if (typeof priceB === 'string') return 1;

            // Numeric comparison for other plans
            return priceA - priceB;
          })
          .map((plan) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={3}
              key={plan.name}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <PlanCard
                plan={plan}
                selectedFrequency={effectiveFrequency}
                currentUserPlan={currentUserPlan}
                pricingData={pricingData}
                handleUpgrade={handleUpgrade}
                onContinue={onContinue}
                onDowngradeToFree={onDowngradeToFree}
              />
            </Grid>
          ))}
      </Grid>
    </Stack>
  );
};

export default Plans;
